/**
 * The setup wizard's tailoring (Stage 5): a few questions about the
 * organisation, answered once, turn the starters into its own templates, so
 * most organisations start work without opening the editor.
 *
 * - **What it regulates** picks the base: the starters, the short "simple"
 *   pair, or the healthcare (HIPAA) or defense (CUI / ITAR) pair from the
 *   library. Terminations and access changes have no variants and stay the
 *   starters'.
 * - **Which departments it has**: a starter's department the organisation
 *   does not keep is handed to one it does ("Facilities' cards go to HR"), or
 *   its cards are left out.
 * - **Remote staff**: none, and the cards only a remote hire gets are left
 *   out.
 *
 * A card left out has its dependencies joined up around it (`bridge()`, the
 * rule materialising uses), so leaving out the middle of A -> B -> C does not
 * let C start before A; reads of its fields and branches on what it records
 * go with it, as they do when a card is removed in the editor. Every result is
 * validated, as anything saved as a template is.
 */

import { bridge } from "./instance.js";
import { TEMPLATE_LIBRARY } from "./library.js";
import { STARTER_TEMPLATES } from "./starters.js";
import type { Condition, Template, TemplateNode } from "./template.js";
import { validateTemplate } from "./validate.js";

/** What the organisation's work is regulated by, which picks the onboarding and offboarding it starts from. */
export type Regime = "standard" | "simple" | "healthcare" | "defense";

export const REGIMES: Record<Regime, string> = {
  standard: "The standard templates",
  simple: "A short version, for a small team",
  healthcare: "Healthcare (HIPAA)",
  defense: "Defense contractor (CUI / ITAR)",
};

export interface Tailoring {
  regime: Regime;
  /** Whether anybody works remotely. */
  remote: boolean;
  /**
   * For each department the starters name that the organisation does not
   * keep: the department that does its part instead, or null to leave its
   * cards out. A department not named here and not kept is left out.
   */
  instead: Record<string, string | null>;
  /** The departments the organisation keeps. */
  departments: readonly string[];
}

const LIBRARY_ID: Record<Exclude<Regime, "standard">, { onboarding: string; offboarding: string }> = {
  simple: { onboarding: "library-simple-onboarding", offboarding: "library-simple-offboarding" },
  healthcare: { onboarding: "library-hipaa-onboarding", offboarding: "library-hipaa-offboarding" },
  defense: { onboarding: "library-defense-onboarding", offboarding: "library-defense-offboarding" },
};

/** The library's variant of a starter for a regime, or the starter itself. */
function baseFor(starter: Template, regime: Regime): Template {
  if (regime === "standard" || (starter.use && starter.use !== "standard")) return starter;
  const wanted = LIBRARY_ID[regime][starter.kind];
  return TEMPLATE_LIBRARY.find((entry) => entry.template.id === wanted)?.template ?? starter;
}

/** True of a card only a remote hire gets: a condition that lets in remote and nothing else. */
function remoteOnly(node: TemplateNode): boolean {
  return (node.conditions ?? []).some(
    (condition) =>
      "field" in condition &&
      condition.field === "employeeType" &&
      condition.op === "in" &&
      condition.values.length > 0 &&
      condition.values.every((value) => value === "remote"),
  );
}

/**
 * The template without these cards: their arrows bridged, and every read,
 * checklist and branch that depended on them gone with them.
 */
export function withoutCards(template: Template, drop: ReadonlySet<string>): Template {
  if (drop.size === 0) return template;
  const kept = new Set(template.nodes.filter((node) => !drop.has(node.key)).map((node) => node.key));
  const keepCondition = (condition: Condition) =>
    !("recorded" in condition) || kept.has(condition.recorded.node);
  return {
    ...template,
    nodes: template.nodes
      .filter((node) => kept.has(node.key))
      .map((node) => {
        const { checklistFrom, inputs, conditions, ...rest } = node;
        return {
          ...rest,
          ...(checklistFrom && kept.has(checklistFrom.node) ? { checklistFrom } : {}),
          ...(inputs ? { inputs: inputs.filter((link) => kept.has(link.node)) } : {}),
          ...(conditions ? { conditions: conditions.filter(keepCondition) } : {}),
        };
      }),
    edges: bridge(kept, template.edges),
  };
}

/** One starter, tailored: the cards handed to other departments, those left out, and why. */
export function tailorTemplate(
  starter: Template,
  tailoring: Tailoring,
): { template: Template; changes: string[] } {
  const base = baseFor(starter, tailoring.regime);
  const changes: string[] = [];
  if (base !== starter) changes.push(`Starts from ${base.name}.`);
  const keeps = new Set(tailoring.departments);
  const to = (department: string): string | null =>
    keeps.has(department) ? department : (tailoring.instead[department] ?? null);

  const drop = new Set<string>();
  const handed = new Map<string, number>();
  const nodes = base.nodes.map((node) => {
    if (!tailoring.remote && remoteOnly(node)) {
      drop.add(node.key);
      return node;
    }
    const owner = to(node.department);
    if (owner === null) {
      drop.add(node.key);
      return node;
    }
    if (owner !== node.department)
      handed.set(`${node.department} to ${owner}`, (handed.get(`${node.department} to ${owner}`) ?? 0) + 1);
    const signoffs = [
      ...new Set(
        (node.signoffs ?? []).map(to).filter((name): name is string => name !== null && name !== owner),
      ),
    ];
    const changed =
      owner !== node.department ||
      signoffs.length !== (node.signoffs ?? []).length ||
      signoffs.some((name, index) => name !== node.signoffs![index]);
    if (!changed) return node;
    const { signoffs: _was, ...rest } = node;
    return { ...rest, department: owner, ...(signoffs.length ? { signoffs } : {}) };
  });
  for (const [move, count] of handed)
    changes.push(`${count === 1 ? "One card" : `${count} cards`}: ${move}.`);
  if (drop.size > 0) changes.push(`${drop.size === 1 ? "One card" : `${drop.size} cards`} left out.`);
  return { template: withoutCards({ ...base, nodes }, drop), changes };
}

/**
 * Every starter, tailored, that comes out different from the starter: these
 * become the organisation's own templates, each in its starter's place. A
 * starter the answers leave as it is stays the starter.
 */
export function tailorStarters(
  tailoring: Tailoring,
): { ok: true; templates: { template: Template; changes: string[] }[] } | { ok: false; reason: string } {
  const out: { template: Template; changes: string[] }[] = [];
  for (const starter of STARTER_TEMPLATES) {
    const tailored = tailorTemplate(starter, tailoring);
    if (tailored.changes.length === 0) continue;
    const problems = validateTemplate(tailored.template);
    if (problems.length > 0)
      return { ok: false, reason: `${starter.name} could not be tailored: ${problems[0]}` };
    out.push({ template: { ...tailored.template, basedOn: starter.id }, changes: tailored.changes });
  }
  return { ok: true, templates: out };
}
